import { useEffect, useMemo, useState } from "react";
import { rawMaterialRepo, deriveCurrentPrices } from "@/services/raw_material.repository";
import type { CurrentPrice, RawMaterialPrice } from "@/domain/material";
import { useMaterialCategories } from "./useMaterialCategories";

/**
 * 원재료 단가 이력 전체 구독 (피벗 테이블 원천 데이터).
 * enabled=false 면 구독하지 않는다 — 단가 읽기 권한자는 원자재·원가·견적 원가 상세 보유자뿐(rules).
 */
export function useRawMaterials(enabled: boolean = true) {
  const [data, setData] = useState<RawMaterialPrice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const unsub = rawMaterialRepo.watchAll(
      (rows) => {
        setData(rows);
        setLoading(false);
        setError(null);
      },
      (err) => {
        setError(err);
        setLoading(false);
      },
    );
    return unsub;
  }, [enabled]);

  return { data, loading: enabled && loading, error };
}

/**
 * "현재 적용단가" — 카테고리 + 이력을 결합한 도출 데이터.
 * @param asOf 기준일 (YYYY-MM-DD). 미지정 시 오늘.
 * @param includeFuture 적용예정 토글 (기본 true)
 * @param enabled false 면 단가를 구독하지 않는다(권한 없는 화면)
 */
export function useCurrentPrices(asOf?: string, includeFuture: boolean = true, enabled: boolean = true) {
  const { data: cats, loading: lc, error: ec } = useMaterialCategories();
  const { data: prices, loading: lp, error: ep } = useRawMaterials(enabled);

  const data: CurrentPrice[] = useMemo(
    () => deriveCurrentPrices(cats, prices, asOf, includeFuture),
    [cats, prices, asOf, includeFuture],
  );

  return {
    data,
    loading: lc || lp,
    error: ec ?? ep ?? null,
  };
}
